'use client'

import { useState } from 'react'
import Link from 'next/link'
import type { ListingSummary } from '@/lib/feedbridge/types'
import { ListingGrid } from '@/components/listings/ListingGrid'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { renderHeading } from '@/utilities/renderHeading'

export interface SuburbListingTab {
  /** Stable tab key — also the value passed to the canonical Tabs component. */
  key: string
  label: string
  /** Where "View All" points for this tab (/buy, /sold, /rent, /leased). */
  href: string
  listings: ListingSummary[]
}

interface SuburbFeaturedListingsProps {
  /** Plain string; `*asterisks*` mark the Merriweather italic run. */
  heading: string
  tabs: SuburbListingTab[]
  /** Keyed by external_listing_id — required for ListingCard to render its bed/bath/car/
   *  land-size spec line (see ListingPage/Component.tsx ~247-255). Covers listings across
   *  all tabs, not just the active one. */
  featuresMap?: Record<string, Record<string, unknown>>
}

/**
 * Suburb profile "Featured Listings" — Figma Frame 454 `4114:172074`.
 *
 * Figma's header is Frame 606 (`4114:172076`, h 104): the displaySm heading (56) with
 * Frame 694 (`4114:172078`) 24px below it, holding the canonical `tabs` instance
 * (283×24 — FOR SALE / SOLD / FOR RENT / LEASED) and a Mini outline "View All"
 * button (67×24) at gap/gap-2 = 8. The card grid starts 64px under that header block
 * (Frame 435 at y=168).
 *
 * The page previously rendered a bare heading over a bespoke grid, with no control row
 * at all. This reuses the canonical Tabs, Button and ListingGrid rather than
 * re-implementing any of them — the same composition `TeamListingsSection` uses on the
 * staff profile, minus the lazy fetching (the suburb page already has the listings
 * server-side, so every panel is pre-populated).
 */
export function SuburbFeaturedListings({
  heading,
  tabs,
  featuresMap,
}: SuburbFeaturedListingsProps) {
  const visibleTabs = tabs.filter((tab) => tab.listings.length > 0)
  const [activeKey, setActiveKey] = useState(visibleTabs[0]?.key ?? '')

  if (!visibleTabs.length) return null

  // Guard against a key that no longer resolves (e.g. an empty tab was dropped).
  const activeTab = visibleTabs.find((tab) => tab.key === activeKey) ?? visibleTabs[0]
  const showTabs = visibleTabs.length > 1

  return (
    // Header block → grid gap: gap/gap-16 64 from tablet up, gap/gap-12 48 at mobile,
    // matching the rest of the suburb page's section rhythm.
    <Tabs
      className="flex flex-col gap-12 md:gap-16"
      hasPanels={showTabs}
      onValueChange={setActiveKey}
      value={activeTab.key}
    >
      <div className="flex flex-col gap-6 items-start">
        {/* Headings/displaySm (4114:172077) — `text-display-3xl` already carries the
            responsive 40/44/48 scale, so no manual md: override. */}
        <h2 className="font-display font-normal text-display-3xl text-foreground">
          {renderHeading(heading)}
        </h2>

        {/* Frame 694 — tabs then "View All" at gap/gap-2 = 8, items-start. */}
        <div className="flex items-start gap-2">
          {showTabs && (
            <TabsList aria-label="Listing type">
              {/* Caps are an instance-level choice at this node (I4114:172079;2198:41440),
                  not part of the canonical tab component. */}
              {visibleTabs.map((tab) => (
                <TabsTrigger className="uppercase" key={tab.key} value={tab.key}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          )}
          {/* 4114:172080 → Button 2198:34629: type=outline, size=Mini (67×24). */}
          <Button asChild className="shrink-0" size="mini" variant="outline">
            <Link href={activeTab.href}>View All</Link>
          </Button>
        </div>
      </div>

      {/* Card grid — canonical ListingGrid, which already carries the Figma gap scale
          (1 col / row 48 mobile · 2 cols / 32 tablet · 3 cols / 64 desktop). */}
      {showTabs ? (
        <TabsContent value={activeTab.key}>
          <ListingGrid featuresMap={featuresMap} listings={activeTab.listings} />
        </TabsContent>
      ) : (
        <div>
          <ListingGrid featuresMap={featuresMap} listings={activeTab.listings} />
        </div>
      )}
    </Tabs>
  )
}
